import {
  AlertLevel,
  CreditRequest_CreateCreditRequestMutation,
  CreditRequest_CreateCreditRequestMutationVariables,
  CreditRequestBatchLineItemInput,
} from '@/types';
import {
  CREDIT_REQUEST_BATCHES_PAGE_LIMIT,
  CREDIT_REQUEST_BATCHES_QUERY,
} from '@/hooks/use-get-credit-request-batches';
import { CreditRequestBatchLineItem } from '@/contexts/credit-request-context-types';
import { gql, useMutation } from '@apollo/client';
import { useUserContext } from '@/contexts/user-context';

export const CREATE_CREDIT_REQUEST_MUTATION = gql`
  mutation CreditRequest_CreateCreditRequest(
    $chatUUID: uuid!
    $creditRequestBatchLineItems: [CreditRequestBatchLineItemInput!]!
  ) {
    createCreditRequest(
      chat_id: $chatUUID
      credit_request_batch_line_items: $creditRequestBatchLineItems
    ) {
      alerts {
        alert_level
        alert_message
      }
      credit_request_batch {
        uuid
      }
    }
  }
`;

export const adaptCreditRequestLineItemToBatchInput = (
  creditRequestBatchLineItems: CreditRequestBatchLineItem[],
): CreditRequestBatchLineItemInput[] =>
  creditRequestBatchLineItems.map((lineItem) => ({
    credit_request_reason_id: lineItem.creditRequestReason.uuid,
    custom_properties: lineItem.customProperties,
    notes: lineItem.notes,
    order_invoice_id: lineItem.orderInvoiceId,
    pack_price_in_micros: lineItem.priceInMicros,
    photo_url_list: lineItem.photoURLs,
    quantity: lineItem.quantity,
    uom_override: lineItem.uomOverride,
    variant_pack_id: lineItem.item.metadata!.variantPackUUID,
  }));

export const useCreateCreditRequest = () => {
  const {
    selectedRestaurantRelationship: { chatUUIDs },
  } = useUserContext();
  const chatUUID = chatUUIDs[0];

  const [createCreditRequest] = useMutation<
    CreditRequest_CreateCreditRequestMutation,
    CreditRequest_CreateCreditRequestMutationVariables
  >(CREATE_CREDIT_REQUEST_MUTATION, {
    refetchQueries: [
      {
        query: CREDIT_REQUEST_BATCHES_QUERY,
        variables: {
          chatUUID,
          limit: CREDIT_REQUEST_BATCHES_PAGE_LIMIT,
          offset: 0,
        },
      },
    ],
  });

  return async (creditRequestBatchLineItems: CreditRequestBatchLineItem[]) => {
    const { data } = await createCreditRequest({
      variables: {
        chatUUID,
        creditRequestBatchLineItems: adaptCreditRequestLineItemToBatchInput(
          creditRequestBatchLineItems,
        ),
      },
    });

    const negativeAlert = (data?.createCreditRequest?.alerts ?? []).find(
      ({ alert_level }) => alert_level === AlertLevel.Negative,
    );

    if (negativeAlert) {
      throw new Error(negativeAlert.alert_message);
    }

    return data?.createCreditRequest?.credit_request_batch?.uuid;
  };
};
